import { ResponsiveGridLayout, useContainerWidth, verticalCompactor } from 'react-grid-layout';
import type { Layout, ResponsiveLayouts } from 'react-grid-layout';
import { WIDGET_MAP } from './widgetRegistry';
import { WidgetCard } from './WidgetCard';
import { ChartWidget } from './ChartWidget';
import { RoleKpiWidget } from './RoleKpiWidget';
import type { WidgetDataMap } from '@/hooks/useWidgetData';

interface Props {
  enabledWidgetIds: string[];
  layouts: ResponsiveLayouts;
  data: WidgetDataMap;
  loading: boolean;
  onLayoutChange: (currentLayout: Layout, allLayouts: ResponsiveLayouts) => void;
  /**
   * Optional per-widget scope overrides (from a role layout). Keyed by widget id
   * → one of 'MY' | 'TEAM' | 'DEPT' | 'ORG'.
   */
  widgetScopes?: Record<string, 'MY' | 'TEAM' | 'DEPT' | 'ORG'>;
}

export function WidgetGrid({ enabledWidgetIds, layouts, data, loading, onLayoutChange, widgetScopes }: Props) {
  const { width, containerRef, mounted } = useContainerWidth();

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <div ref={containerRef as any} style={{ position: 'relative' }}>
      {mounted && width > 0 && (
        <ResponsiveGridLayout
          key={enabledWidgetIds.join(',')}
          width={width}
          layouts={layouts}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 4, md: 3, sm: 2, xs: 1, xxs: 1 }}
          rowHeight={110}
          margin={[16, 16] as const}
          containerPadding={[0, 0] as const}
          dragConfig={{ enabled: true, handle: '.widget-drag-handle' }}
          resizeConfig={{ enabled: true, handles: ['se'] }}
          compactor={verticalCompactor}
          onLayoutChange={onLayoutChange}
        >
          {enabledWidgetIds.map((id) => {
            const widget = WIDGET_MAP[id];
            if (!widget) return null;

            // Self-fetching role-KPI widgets
            if (widget.endpoint) {
              return (
                <div key={id} style={{ height: '100%' }}>
                  <RoleKpiWidget widget={widget} scope={widgetScopes?.[id]} />
                </div>
              );
            }

            if (widget.chartType === 'bar') {
              return (
                <div key={id} style={{ height: '100%' }}>
                  <ChartWidget />
                </div>
              );
            }

            return (
              <div key={id} style={{ height: '100%' }}>
                <WidgetCard widget={widget} data={data} loading={loading} />
              </div>
            );
          })}
        </ResponsiveGridLayout>
      )}
    </div>
  );
}
